import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useResponsive } from '@/hooks/useResponsive';
import { useDownloadInfo } from '@/pages/Download/hooks/useDownloadInfo';
import { openDownloadInNewWindow, safeDownload } from '@/utils/downloadHelper';
import {
  getRecommendedDownloadType,
  getSystemInfo,
  isSystemSupported,
} from '@/utils/systemDetector';
import { showNoSuitableVersionToast } from '@/utils/toastHelper';
import { isMobileByUA } from '@/utils/ua';
import { sendDownloadClickEvent, sendWeblogEvent } from '@/utils/weblogger';

import SmartDownloadIcon from '../../Icons/SmartDownloadIcon';
import styles from './index.module.css';
import Static_bg from './portal_banner_bg/Static_bg.svg';
import box_1 from './portal_banner_bg/box_1.png';
import box_2 from './portal_banner_bg/box_2.png';
import box_3 from './portal_banner_bg/box_3.png';
import box_4 from './portal_banner_bg/box_4.png';
import box_5 from './portal_banner_bg/box_5.png';
import box_6 from './portal_banner_bg/box_6.png';
import box_7 from './portal_banner_bg/box_7.png';
import box_8 from './portal_banner_bg/box_8.png';
import box_9 from './portal_banner_bg/box_9.png';
import box_10 from './portal_banner_bg/box_10.png';
import box_11 from './portal_banner_bg/box_11.png';
import box_12 from './portal_banner_bg/box_12.png';
import box_13 from './portal_banner_bg/box_13.png';
import box_14 from './portal_banner_bg/box_14.png';
import box_15 from './portal_banner_bg/box_15.png';
import box_16 from './portal_banner_bg/box_16.png';
import box_Symbol_1 from './portal_banner_bg/box_Symbol_1.png';
import box_Symbol_2 from './portal_banner_bg/box_Symbol_2.png';
import box_Symbol_3 from './portal_banner_bg/box_Symbol_3.png';
import box_Symbol_4 from './portal_banner_bg/box_Symbol_4.png';

// 图片分组
const symbolImages = [box_Symbol_1, box_Symbol_2, box_Symbol_3, box_Symbol_4];
const boxImages1 = [box_1, box_2, box_3, box_4, box_5, box_6, box_7, box_8];
const boxImages2 = [box_9, box_10, box_11, box_12, box_13, box_14, box_15, box_16];

// 工具函数
const getRandomInterval = () => Math.random() * (10000 - 3000) + 3000; // 3-10秒
const getRandomIntervalBox = () => Math.random() * (5000 - 3000) + 3000; // 3-5秒

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const { downloadInfo } = useDownloadInfo();
  const [recommendedType, setRecommendedType] = useState<string | null>(null);
  const { isMobile } = useResponsive();

  // 底图引用与加载完成回调：加载后降低层级，避免遮挡
  const bottomImageRef = useRef<HTMLImageElement | null>(null);
  const handleBottomImageLoaded = useCallback(() => {
    const el = bottomImageRef.current;
    if (el) {
      el.classList.add(styles.bottomImageLoaded);
    }
  }, []);

  // 初始化时检测用户系统
  useEffect(() => {
    const systemInfo = getSystemInfo();
    const recommendedDownloadType = getRecommendedDownloadType();
    setRecommendedType(recommendedDownloadType);

    // 在开发环境下输出调试信息
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('检测到的系统信息:', systemInfo);
      // eslint-disable-next-line no-console
      console.log('推荐下载类型:', recommendedDownloadType);
    }
  }, []);

  // 处理 All Download 按钮点击 - 跳转到下载页面
  const handleAllDownload = useCallback(() => {
    // 埋点上报：HeroSection All Download按钮点击
    sendDownloadClickEvent('all_platforms', 'redirect', 'home');

    navigate('/download');
  }, [navigate]);

  // 处理 Download 按钮点击 - 直接下载
  const handleDirectDownload = useCallback(async () => {
    if (isMobileByUA() && downloadInfo?.arm) {
      safeDownload(downloadInfo.arm);
      return;
    }

    if (!downloadInfo || !recommendedType) {
      // 如果没有下载信息或无法检测系统，跳转到下载页面
      sendDownloadClickEvent(recommendedType || 'unknown', 'redirect_no_info', 'home');

      // 显示无合适版本的提示
      showNoSuitableVersionToast();
      navigate('/download');
      return;
    }

    let targetDownloadInfo = null;
    let platformName = '';

    // 根据推荐类型获取下载信息
    switch (recommendedType) {
      case 'apple':
        targetDownloadInfo = downloadInfo.arm;
        platformName = 'apple';
        break;
      case 'intel':
        targetDownloadInfo = downloadInfo.intel;
        platformName = 'intel';
        break;
      case 'windows':
        targetDownloadInfo = downloadInfo.windows;
        platformName = 'windows';
        break;
      default:
        // 不支持的系统，跳转到下载页面
        sendDownloadClickEvent('unsupported', 'redirect_unsupported', 'home');
        navigate('/download');
        return;
    }

    if (targetDownloadInfo) {
      try {
        // 尝试安全下载
        const downloadSuccess = await safeDownload(targetDownloadInfo);

        if (downloadSuccess) {
          // 下载成功时的埋点上报
          sendDownloadClickEvent(platformName, targetDownloadInfo.version || 'unknown', 'home');
        } else {
          // 如果下载失败，尝试在新窗口打开
          const downloadUrl = targetDownloadInfo.dmgUrl || targetDownloadInfo.url;
          if (downloadUrl) {
            // 新窗口下载的埋点上报
            sendDownloadClickEvent(platformName, targetDownloadInfo.version || 'unknown', 'home');

            openDownloadInNewWindow(downloadUrl);
          } else {
            // 最后的备用方案：跳转到下载页面
            sendDownloadClickEvent(platformName, 'redirect_no_url', 'home');

            navigate('/download');
          }
        }
      } catch (error) {
        console.error('下载失败:', error);
        sendDownloadClickEvent(platformName, 'redirect_error', 'home');

        navigate('/download');
      }
    } else {
      // 没有下载信息，跳转到下载页面
      sendDownloadClickEvent(platformName, 'redirect_no_target', 'home');

      navigate('/download');
    }
  }, [downloadInfo, recommendedType, navigate]);

  // 检查是否支持当前系统
  const systemSupported = isMobile ? true : isSystemSupported();

  // 元素引用，直接切换 class，避免频繁组件重渲染
  const symbolRefs = useRef<(HTMLImageElement | null)[]>([]);
  const box1Refs = useRef<(HTMLImageElement | null)[]>([]);
  const box2Refs = useRef<(HTMLImageElement | null)[]>([]);

  // 动画控制状态
  const symbolPlayingRef = useRef<boolean[]>([false, false, false, false]);
  const boxPlayingCountRef = useRef({ count1: 0, count2: 0 });
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // Symbol 动画控制
  const startSymbolAnimation = (index: number) => {
    const playAnimation = () => {
      // 检查是否有其他 Symbol 正在播放
      const isAnySymbolPlaying = symbolPlayingRef.current.some(
        (playing, i) => playing && i !== index,
      );
      if (isAnySymbolPlaying) {
        const retryDelay = getRandomInterval();
        const timeout = setTimeout(playAnimation, retryDelay);
        timeoutsRef.current.push(timeout);
        return;
      }

      // 开始播放（切 class，避免 setState 带来的全组件重渲染）
      symbolPlayingRef.current[index] = true;
      const el = symbolRefs.current[index];
      if (el) {
        el.classList.add(styles.symbolAnimate);
      }

      // 6秒后结束动画
      const timeout = setTimeout(() => {
        symbolPlayingRef.current[index] = false;
        const el = symbolRefs.current[index];
        if (el) {
          el.classList.remove(styles.symbolAnimate);
        }

        // 随机间隔后开始下一个动画
        const randomInterval = getRandomInterval();
        const nextTimeout = setTimeout(playAnimation, randomInterval);
        timeoutsRef.current.push(nextTimeout);
      }, 6000);
      timeoutsRef.current.push(timeout);
    };

    // 初始随机延迟
    const initialDelay = 0;
    const timeout = setTimeout(playAnimation, initialDelay);
    timeoutsRef.current.push(timeout);
  };

  // Box 动画控制
  const startBoxAnimation = (index: number, group: 1 | 2) => {
    const playAnimation = () => {
      const maxConcurrent = 3;
      const currentCount =
        group === 1 ? boxPlayingCountRef.current.count1 : boxPlayingCountRef.current.count2;

      // 检查是否已达到最大并发数
      if (currentCount >= maxConcurrent) {
        const retryDelay = getRandomIntervalBox();
        const timeout = setTimeout(playAnimation, retryDelay);
        timeoutsRef.current.push(timeout);
        return;
      }

      // 开始播放
      if (group === 1) {
        boxPlayingCountRef.current.count1++;
      } else {
        boxPlayingCountRef.current.count2++;
      }

      // 直接切换目标元素的 class
      const refArr = group === 1 ? box1Refs.current : box2Refs.current;
      const el = refArr[index];
      if (el) {
        el.classList.add(group === 1 ? styles.boxAnimate1 : styles.boxAnimate2);
      }

      // 3秒后结束动画
      const timeout = setTimeout(() => {
        if (group === 1) {
          boxPlayingCountRef.current.count1--;
        } else {
          boxPlayingCountRef.current.count2--;
        }

        const refArr = group === 1 ? box1Refs.current : box2Refs.current;
        const el = refArr[index];
        if (el) {
          el.classList.remove(group === 1 ? styles.boxAnimate1 : styles.boxAnimate2);
        }

        // 随机间隔后开始下一个动画
        const randomInterval = getRandomIntervalBox();
        const nextTimeout = setTimeout(playAnimation, randomInterval);
        timeoutsRef.current.push(nextTimeout);
      }, 3000);
      timeoutsRef.current.push(timeout);
    };

    // 初始随机延迟
    const initialDelay = 0;
    const timeout = setTimeout(playAnimation, initialDelay);
    timeoutsRef.current.push(timeout);
  };

  // 启动所有动画
  useEffect(() => {
    // 启动 Symbol 动画
    symbolImages.forEach((_, index) => {
      startSymbolAnimation(index);
    });

    // 启动 Box 动画
    boxImages1.forEach((_, index) => {
      startBoxAnimation(index, 1);
    });

    boxImages2.forEach((_, index) => {
      startBoxAnimation(index, 2);
    });

    // 清理函数
    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current = [];
    };
  }, []);

  return (
    <section className={styles.heroSection}>
      {/* 静态背景：提高加载优先级，避免首屏黑屏 */}
      <img
        src={Static_bg}
        alt="Background"
        className={styles.bottomImage}
        loading="eager"
        decoding="async"
        fetchPriority="high"
        ref={bottomImageRef}
        onLoad={handleBottomImageLoaded}
      />

      {/* Symbol 动画层 */}
      {symbolImages.map((image, index) => (
        <img
          key={`symbol-${index}`}
          ref={(el) => (symbolRefs.current[index] = el)}
          src={image}
          alt={`Symbol ${index + 1}`}
          className={`${styles.symbolImage} ${styles[`symbol${index + 1}`]}`}
        />
      ))}

      {/* Box 1-8 动画层 */}
      {boxImages1.map((image, index) => (
        <img
          key={`box1-${index}`}
          ref={(el) => (box1Refs.current[index] = el)}
          src={image}
          alt={`Box ${index + 1}`}
          className={`${styles.boxImage1} ${styles[`box${index + 1}`]}`}
        />
      ))}

      {/* Box 9-16 动画层 */}
      {boxImages2.map((image, index) => (
        <img
          key={`box2-${index}`}
          ref={(el) => (box2Refs.current[index] = el)}
          src={image}
          alt={`Box ${index + 9}`}
          className={`${styles.boxImage2} ${styles[`box${index + 9}`]}`}
        />
      ))}

      <div className={styles.heroBackground}>
        <div className={styles.heroContainer}>
          <div className={styles.heroContent}>
            <div className={styles.textContent}>
              <h1 className={styles.heroTitle}>Code Begins with a Flicker</h1>
              <p className={styles.heroDescription}>
                An AI-powered IDE that turns inspiration into reality, fuels boundless creativity,
                and stays ready whenever imagination strikes.
              </p>
            </div>
            <div className={`${styles.buttonGroup} ${isMobile ? styles.mobileButtonGroup : ''}`}>
              <button
                className={`${styles.downloadButton} ${isMobile ? styles.mobileDownloadButton : ''}`}
                onClick={handleDirectDownload}
                disabled={!systemSupported}
                title={
                  systemSupported
                    ? 'Download the version suitable for your system'
                    : 'Current system is not supported, please view all download options'
                }
              >
                <SmartDownloadIcon recommendedType={recommendedType} />
                <span>Download</span>
              </button>

              <button
                className={`${styles.allDownloadButton} ${isMobile ? styles.mobileAllDownloadButton : ''}`}
                onClick={handleAllDownload}
              >
                <span>All Downloads</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
